import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { CommonReduxState } from '@src/models/commons/reduxState.interface';
import { resolveElasticDivisionIds } from '../divisions/divisionIdsUtils';
import useHasOrgWideAccess from './useHasOrgWideAccess';

/**
 * Division ids for the Elastic generic-query API, derived from the divisions
 * selector. A full selection collapses to `[]` ("all divisions of the org")
 * only for org-wide roles with more than one allowed division; a single
 * reachable division stays explicit. See {@link resolveElasticDivisionIds}.
 */
const useElasticDivisionIds = (): string[] => {
  const selectedDivsIds = useSelector((state: CommonReduxState) => state?.divisions?.selector?.ids);
  const assignedDivisions = useSelector(
    (state: CommonReduxState) => state?.divisions?.assigned?.data?.divisions,
  );

  const allowedDivsIds = useMemo(
    () => Object.values(assignedDivisions ?? {}).map(division => division.id),
    [assignedDivisions],
  );

  const orgWide = useHasOrgWideAccess();

  return useMemo(
    () => resolveElasticDivisionIds(selectedDivsIds ?? [], allowedDivsIds, orgWide),
    [selectedDivsIds, allowedDivsIds, orgWide],
  );
};

export default useElasticDivisionIds;
